import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Typography } from '@mui/material';
import { Language } from '../types/app';
import { AppBreadcrumbs, ContentStateHandler } from '../components/common';
import ArticleViewer from '../components/ApiDetail/GuidesTab/ArticleViewer';
import {
  fetchArticles,
  selectLocalizedArticles,
  selectArticlesLoading,
  selectArticlesError,
  selectHasArticles,
} from '../store/articles';
import { fetchApiDefinitionById, currentApiDefinitionSelector } from '../store/apiDetails';
import { getLocalizedContent } from '../utils/apiUtils';
import { RootState } from '../store/types';
import { AppDispatch } from '../store';

const ArticleView: React.FC = () => {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { slug: apiSlug, articleSlug } = useParams<{ slug: string; articleSlug: string }>();

  const currentLanguage: Language = i18n.language as Language;
  const localizedArticles = useSelector((state: RootState) =>
    selectLocalizedArticles(state, apiSlug || '', currentLanguage),
  );
  const loading = useSelector((state: RootState) => selectArticlesLoading(state, apiSlug || ''));
  const error = useSelector(selectArticlesError);
  const hasArticles = useSelector((state: RootState) => selectHasArticles(state, apiSlug || ''));

  const currentApiDefinition = useSelector((state: RootState) =>
    currentApiDefinitionSelector(state, apiSlug),
  );

  const selectedArticle = localizedArticles.find((article) => article.slug === articleSlug);

  useEffect(() => {
    if (apiSlug && !currentApiDefinition) {
      dispatch(fetchApiDefinitionById(apiSlug));
    }
    if (apiSlug && !hasArticles) {
      dispatch(fetchArticles(apiSlug));
    }
  }, [dispatch, apiSlug, currentApiDefinition, hasArticles]);

  const getBreadcrumbItems = () => {
    const items: Array<{ label: string; onClick?: () => void }> = [
      {
        label: t('common.apis'),
        onClick: () => navigate('/'),
      },
    ];

    if (currentApiDefinition) {
      const localizedContent = getLocalizedContent(currentApiDefinition, currentLanguage);
      items.push({
        label: localizedContent?.title || t('apiDetail.fallbackTitle'),
        onClick: () => navigate(`/api-definitions/${apiSlug}?tab=guides`),
      });
    }

    if (selectedArticle) {
      items.push({
        label: selectedArticle.title,
      });
    }

    return items;
  };

  if (!apiSlug) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant='h6' color='error'>
          {t('apiDetail.specsTab.apiIdRequired')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <AppBreadcrumbs items={getBreadcrumbItems()} />
      <ContentStateHandler
        loading={loading}
        error={error}
        hasContent={!!selectedArticle}
        emptyMessage={t('apiDetail.guidesTab.noArticles')}
      >
        {selectedArticle && <ArticleViewer article={selectedArticle} />}
      </ContentStateHandler>
    </Box>
  );
};

export default ArticleView;
